import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { LangProvider } from "@/lib/i18n";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import AuthCallback from "@/components/AuthCallback";
import Layout from "@/components/Layout";
import Home from "@/pages/Home";
import PackagesPage from "@/pages/PackagesPage";
import GalleryPage from "@/pages/GalleryPage";
import BookingFlow from "@/pages/BookingFlow";
import InquiryPage from "@/pages/InquiryPage";
import TrackPage from "@/pages/TrackPage";
import ContactPage from "@/pages/ContactPage";
import Account from "@/pages/Account";
import AdminLogin from "@/pages/admin/AdminLogin";
import AdminLayout from "@/pages/admin/AdminLayout";
import Dashboard from "@/pages/admin/Dashboard";
import Bookings from "@/pages/admin/Bookings";
import PackagesAdmin from "@/pages/admin/PackagesAdmin";
import CalendarMgmt from "@/pages/admin/CalendarMgmt";
import Inquiries from "@/pages/admin/Inquiries";
import Vendors from "@/pages/admin/Vendors";
import GalleryAdmin from "@/pages/admin/GalleryAdmin";
import Invoices from "@/pages/admin/Invoices";
import Reports from "@/pages/admin/Reports";
import Reviews from "@/pages/admin/Reviews";

function ProtectedAdmin({ children }) {
  const { user, loading } = useAuth();
  if (loading)
    return (
      <div className="min-h-screen bg-background flex items-center justify-center text-gold tracking-widest">
        Loading…
      </div>
    );
  if (!user) return <Navigate to="/admin/login" replace />;
  if (!user.is_admin)
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center gap-4 text-center px-6">
        <p className="text-xl text-white">You are not authorized to access the admin panel.</p>
        <a href="/" className="text-gold underline">Go to website</a>
      </div>
    );
  return children;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route element={<Layout />}>
        <Route path="/" element={<Home />} />
        <Route path="/packages" element={<PackagesPage />} />
        <Route path="/gallery" element={<GalleryPage />} />
        <Route path="/book/:slug" element={<BookingFlow />} />
        <Route path="/inquiry" element={<InquiryPage />} />
        <Route path="/track" element={<TrackPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/account" element={<Account />} />
      </Route>
      <Route path="/admin/login" element={<AdminLogin />} />
      <Route
        path="/admin"
        element={
          <ProtectedAdmin>
            <AdminLayout />
          </ProtectedAdmin>
        }
      >
        <Route index element={<Dashboard />} />
        <Route path="bookings" element={<Bookings />} />
        <Route path="packages" element={<PackagesAdmin />} />
        <Route path="calendar" element={<CalendarMgmt />} />
        <Route path="inquiries" element={<Inquiries />} />
        <Route path="vendors" element={<Vendors />} />
        <Route path="gallery" element={<GalleryAdmin />} />
        <Route path="invoices" element={<Invoices />} />
        <Route path="reports" element={<Reports />} />
        <Route path="reviews" element={<Reviews />} />
      </Route>
    </Routes>
  );
}

export default function App() {
  return (
    <LangProvider>
      <AuthProvider>
        <BrowserRouter>
          <Toaster position="top-center" theme="dark" richColors />
          <AppRouter />
        </BrowserRouter>
      </AuthProvider>
    </LangProvider>
  );
}
